// src/design-system/components/richTextEditor/MediaUploadModal.tsx
import { useState, useMemo } from "react";
import { Image, Link2, Film, FileText } from "lucide-react";
import * as yup from "yup";
import { Modal } from "../modal/modal";
import { Input } from "../input/Input";
import { Tabs, type TabItem } from "../tabs/tabs";
import { MediaUpload } from "../media-upload/MediaUpload";
import { colors } from "../../theme/colors";
import type { MediaUploadModalProps, MediaUploadType } from "./types";
import {
  MEDIA_UPLOAD_ACCEPTED_FORMATS,
  MEDIA_UPLOAD_MAX_FILE_SIZE,
} from "@/utils/constants/mediaUploadConstants";

// Media type configurations
const MEDIA_TYPE_CONFIG: Record<
  MediaUploadType,
  {
    title: string;
    icon: React.ReactNode;
    extensions: readonly string[];
    urlPatterns: string[];
    helperText: string;
    altLabel: string;
  }
> = {
  image: {
    title: "Insert Image",
    icon: <Image size={20} />,
    extensions: MEDIA_UPLOAD_ACCEPTED_FORMATS.IMAGE,
    urlPatterns: [
      "imgur",
      "cloudinary",
      "unsplash",
      "pexels",
      "giphy",
      "googleapis",
      "cloudfront",
      "s3.amazonaws",
      "/image",
      "/img",
      "/photo",
    ],
    helperText: `Supports: ${MEDIA_UPLOAD_ACCEPTED_FORMATS.IMAGE.map(e => e.toUpperCase()).join(", ")} (Max: ${MEDIA_UPLOAD_MAX_FILE_SIZE.IMAGE}MB)`,
    altLabel: "Alt Text (Optional)",
  },
  video: {
    title: "Insert Video",
    icon: <Film size={20} />,
    extensions: MEDIA_UPLOAD_ACCEPTED_FORMATS.VIDEO,
    urlPatterns: [
      "youtube",
      "vimeo",
      "dailymotion",
      "twitch",
      "cloudinary",
      "cloudfront",
      "s3.amazonaws",
      "/video",
      "/media",
    ],
    helperText: `Supports: ${MEDIA_UPLOAD_ACCEPTED_FORMATS.VIDEO.map(e => e.toUpperCase()).join(", ")} (Max: ${MEDIA_UPLOAD_MAX_FILE_SIZE.VIDEO}MB)`,
    altLabel: "Description (Optional)",
  },
  document: {
    title: "Insert Document",
    icon: <FileText size={20} />,
    extensions: MEDIA_UPLOAD_ACCEPTED_FORMATS.DOCUMENT,
    urlPatterns: [
      "docs.google",
      "dropbox",
      "onedrive",
      "sharepoint",
      "cloudfront",
      "s3.amazonaws",
      "/document",
      "/file",
      "/doc",
    ],
    helperText: `Supports: ${MEDIA_UPLOAD_ACCEPTED_FORMATS.DOCUMENT.map(e => e.toUpperCase()).join(", ")} (Max: ${MEDIA_UPLOAD_MAX_FILE_SIZE.DOCUMENT}MB)`,
    altLabel: "Description (Optional)",
  },
  any: {
    title: "Insert Media",
    icon: <Image size={20} />,
    extensions: [
      ...MEDIA_UPLOAD_ACCEPTED_FORMATS.IMAGE,
      ...MEDIA_UPLOAD_ACCEPTED_FORMATS.VIDEO,
      ...MEDIA_UPLOAD_ACCEPTED_FORMATS.DOCUMENT,
    ],
    urlPatterns: [
      "imgur",
      "cloudinary",
      "unsplash",
      "pexels",
      "giphy",
      "youtube",
      "vimeo",
      "googleapis",
      "cloudfront",
      "s3.amazonaws",
      "dropbox",
      "onedrive",
      "/image",
      "/img",
      "/photo",
      "/video",
      "/media",
      "/document",
      "/file",
    ],
    helperText: "Supports: Images, Videos, Documents",
    altLabel: "Description (Optional)",
  },
};

// Create URL validation schema based on media type
const createUrlSchema = (mediaType: MediaUploadType) => {
  const config = MEDIA_TYPE_CONFIG[mediaType];

  return yup.object().shape({
    mediaUrl: yup
      .string()
      .required("URL is required")
      .test("has-content", "URL is required", (value) => {
        return !!value?.trim();
      })
      .test("valid-url", "Please enter a valid URL", (value) => {
        if (!value?.trim()) return false;

        let urlToTest = value.trim();

        // Add protocol if missing
        if (!/^https?:\/\//i.test(urlToTest)) {
          urlToTest = `https://${urlToTest}`;
        }

        try {
          const url = new URL(urlToTest);
          // Must be http or https
          if (!["http:", "https:"].includes(url.protocol)) {
            return false;
          }
          // Must have a valid hostname
          if (!url.hostname || url.hostname.length < 1) {
            return false;
          }
          // Basic domain validation (must have at least one dot for TLD, unless localhost)
          if (url.hostname !== "localhost" && !url.hostname.includes(".")) {
            return false;
          }
          return true;
        } catch {
          return false;
        }
      })
      .test("no-spaces", "URL cannot contain spaces", (value) => {
        if (!value) return true;
        return !value.includes(" ");
      })
      .test("valid-characters", "URL contains invalid characters", (value) => {
        if (!value?.trim()) return true;
        const invalidChars = /[<>{}|\\^`[\]]/;
        return !invalidChars.test(value);
      })
      .test("media-url", `URL should point to a valid ${mediaType === "any" ? "media" : mediaType} file`, (value) => {
        if (!value?.trim()) return false;

        let urlToTest = value.trim().toLowerCase();

        // Add protocol if missing for URL parsing
        if (!/^https?:\/\//i.test(urlToTest)) {
          urlToTest = `https://${urlToTest}`;
        }

        try {
          const url = new URL(urlToTest);
          const pathname = url.pathname.toLowerCase();

          // Check if URL ends with valid extension
          const hasValidExtension = config.extensions.some((ext) =>
            pathname.endsWith(`.${ext}`)
          );

          // Allow URLs with valid extensions OR common hosting patterns
          const isHostingPattern = config.urlPatterns.some(
            (pattern) =>
              url.hostname.includes(pattern) || pathname.includes(pattern)
          );

          // Accept if it has valid extension or matches hosting patterns
          // Also accept data URLs for base64
          return (
            hasValidExtension ||
            isHostingPattern ||
            value.trim().startsWith("data:image/") ||
            value.trim().startsWith("data:video/") ||
            value.trim().startsWith("data:application/")
          );
        } catch {
          return false;
        }
      }),
    altText: yup
      .string()
      .max(100, "Description must be 100 characters or less")
      .test("valid-alt-text", "Description contains invalid characters", (value) => {
        if (!value) return true;
        // Prevent XSS-like patterns
        const invalidPatterns = /<script|javascript:|on\w+=/i;
        return !invalidPatterns.test(value);
      }),
  });
};

// Alt text only schema for upload tab
const uploadAltTextSchema = yup.object().shape({
  altText: yup
    .string()
    .max(100, "Description must be 100 characters or less")
    .test("valid-alt-text", "Description contains invalid characters", (value) => {
      if (!value) return true;
      const invalidPatterns = /<script|javascript:|on\w+=/i;
      return !invalidPatterns.test(value);
    }),
});

// Helper to ensure URL has protocol
const ensureProtocol = (url: string): string => {
  const trimmedUrl = url.trim();
  if (!/^https?:\/\//i.test(trimmedUrl)) {
    return `https://${trimmedUrl}`;
  }
  return trimmedUrl;
};

// Map MediaUploadType to MediaUpload's mediaType
const getMediaUploadType = (type: MediaUploadType): "image" | "video" | "document" | "any" => {
  return type;
};

export const MediaUploadModal = ({
  open,
  onClose,
  onConfirm,
  onMediaUpload,
  onImageUpload, // Backward compatibility
  mediaType = "any",
}: MediaUploadModalProps & { onImageUpload?: (file: File) => Promise<string> }) => {
  const [activeTab, setActiveTab] = useState<string>("upload");
  const [loading, setLoading] = useState(false);
  const [mediaUrl, setMediaUrl] = useState("");
  const [altText, setAltText] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [altTextError, setAltTextError] = useState<string | null>(null);
  const [urlTouched, setUrlTouched] = useState(false);
  const [altTextTouched, setAltTextTouched] = useState(false);

  // MediaUpload state
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const config = MEDIA_TYPE_CONFIG[mediaType];
  const urlSchema = useMemo(() => createUrlSchema(mediaType), [mediaType]);

  // Use onMediaUpload or fallback to onImageUpload for backward compatibility
  const uploadHandler = onMediaUpload || onImageUpload;

  // Validate URL tab form
  const isUrlTabValid = useMemo(() => {
    try {
      urlSchema.validateSync({ mediaUrl, altText });
      return true;
    } catch {
      return false;
    }
  }, [mediaUrl, altText, urlSchema]);

  // Validate upload tab alt text
  const isUploadAltTextValid = useMemo(() => {
    try {
      uploadAltTextSchema.validateSync({ altText });
      return true;
    } catch {
      return false;
    }
  }, [altText]);

  // Validate URL field
  const validateUrl = (value: string) => {
    try {
      urlSchema.validateSync({ mediaUrl: value, altText });
      setUrlError(null);
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        // Only show URL errors
        if (err.path === "mediaUrl") {
          setUrlError(err.message);
        }
      }
    }
  };

  // Validate alt text field
  const validateAltText = (value: string, forUrlTab: boolean = false) => {
    try {
      if (forUrlTab) {
        urlSchema.validateSync({ mediaUrl, altText: value });
      } else {
        uploadAltTextSchema.validateSync({ altText: value });
      }
      setAltTextError(null);
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        if (err.path === "altText") {
          setAltTextError(err.message);
        }
      }
    }
  };

  const handleUrlChange = (value: string) => {
    setMediaUrl(value);
    if (urlTouched) {
      validateUrl(value);
    }
  };

  const handleUrlBlur = () => {
    setUrlTouched(true);
    validateUrl(mediaUrl);
  };

  const handleAltTextChange = (value: string) => {
    setAltText(value);
    if (altTextTouched) {
      validateAltText(value, activeTab === "url");
    }
  };

  const handleAltTextBlur = () => {
    setAltTextTouched(true);
    validateAltText(altText, activeTab === "url");
  };

  const handleFileChange = async (selectedFile: File | null) => {
    setFile(selectedFile);

    if (selectedFile) {
      // Validate alt text before proceeding
      if (!isUploadAltTextValid) {
        setAltTextTouched(true);
        validateAltText(altText, false);
        return;
      }

      try {
        setLoading(true);
        let mediaUrlResult: string;

        if (uploadHandler) {
          // The upload handler (from useEditorMediaUpload hook) returns a blob URL
          // for instant preview and handles the actual upload in the background.
          mediaUrlResult = await uploadHandler(selectedFile);
        } else {
          mediaUrlResult = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(selectedFile);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
          });
        }

        onConfirm(mediaUrlResult, altText || selectedFile.name);
        handleClose();
      } catch (error) {
        console.error("Media upload error:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUrlSubmit = async () => {
    setUrlTouched(true);
    setAltTextTouched(true);

    try {
      urlSchema.validateSync({ mediaUrl, altText });
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        if (err.path === "mediaUrl") {
          setUrlError(err.message);
        } else if (err.path === "altText") {
          setAltTextError(err.message);
        }
      }
      return;
    }

    const processedUrl = ensureProtocol(mediaUrl);

    setLoading(true);
    try {
      onConfirm(processedUrl, altText);
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMediaUrl("");
    setAltText("");
    setUrlError(null);
    setAltTextError(null);
    setUrlTouched(false);
    setAltTextTouched(false);
    setFile(null);
    setPreviewUrl(null);
    setActiveTab("upload");
    onClose();
  };

  const handleOk = () => {
    if (activeTab === "url") {
      handleUrlSubmit();
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    // Reset errors when switching tabs
    setUrlError(null);
    setAltTextError(null);
    setUrlTouched(false);
    setAltTextTouched(false);
  };

  // Character count for alt text
  const altTextCharCount = altText.length;
  const altTextMaxChars = 100;

  const tabItems: TabItem[] = [
    {
      key: "upload",
      label: "Upload",
      icon: config.icon,
      children: (
        <div className="py-4 space-y-4">
          <MediaUpload
            mediaType={getMediaUploadType(mediaType)}
            variant="default"
            size="medium"
            file={file}
            onFileChange={handleFileChange}
            previewUrl={previewUrl}
            onPreviewUrlChange={setPreviewUrl}
            showPreview={true}
            showFileInfo={true}
            placeholder={`Click or drag ${mediaType === "any" ? "file" : mediaType} to upload`}
            helperText={config.helperText}
          />

          <div>
            <Input
              label={config.altLabel}
              placeholder={`Describe the ${mediaType === "any" ? "file" : mediaType} for accessibility`}
              value={altText}
              onChange={(e) => handleAltTextChange(e.target.value)}
              onBlur={handleAltTextBlur}
              error={altTextTouched && altTextError ? altTextError : undefined}
              maxLength={altTextMaxChars}
            />
            <div
              className="text-xs mt-1 text-right"
              style={{
                color:
                  altTextCharCount > altTextMaxChars
                    ? colors.error
                    : colors.textMuted,
              }}
            >
              {altTextCharCount}/{altTextMaxChars} characters
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "url",
      label: "From URL",
      icon: <Link2 size={16} />,
      children: (
        <div className="py-4 space-y-4">
          <Input
            label={`${mediaType === "any" ? "Media" : mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} URL`}
            required
            placeholder={`https://example.com/${mediaType === "any" ? "file" : mediaType}.${config.extensions[0]}`}
            value={mediaUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            onBlur={handleUrlBlur}
            error={urlTouched && urlError ? urlError : undefined}
            prefix={<Link2 size={16} style={{ color: colors.textMuted }} />}
          />

          <div>
            <Input
              label={config.altLabel}
              placeholder={`Describe the ${mediaType === "any" ? "file" : mediaType} for accessibility`}
              value={altText}
              onChange={(e) => handleAltTextChange(e.target.value)}
              onBlur={handleAltTextBlur}
              error={altTextTouched && altTextError ? altTextError : undefined}
              maxLength={altTextMaxChars}
            />
            <div
              className="text-xs mt-1 text-right"
              style={{
                color:
                  altTextCharCount > altTextMaxChars
                    ? colors.error
                    : colors.textMuted,
              }}
            >
              {altTextCharCount}/{altTextMaxChars} characters
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={config.title}
      icon={config.icon}
      size="medium"
      onOk={activeTab === "url" ? handleOk : undefined}
      onCancel={handleClose}
      okText="Insert"
      cancelText="Cancel"
      confirmLoading={loading}
      showFooter={activeTab === "url"}
      okButtonProps={{ disabled: !isUrlTabValid }}
    >
      <div className="px-6 pb-2">
        <Tabs
          items={tabItems}
          activeKey={activeTab}
          onChange={handleTabChange}
          size="medium"
          type="line"
        />
      </div>
    </Modal>
  );
};

// Backward compatibility - export ImageUploadModal as alias
export const ImageUploadModal = MediaUploadModal;

export default MediaUploadModal;
